import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { apiCall } from '../api';

export default function PolicyMaintenance() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseMessage, setLicenseMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleReloadPolicies = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiCall('TCAdminPopulatePolicyCatalog', 'POST');
      if (response.success) {
        setMessage({
          type: 'success',
          text: `Policy catalog reloaded successfully. Processed ${response.policiesProcessed} policies, inserted ${response.policiesInserted} new policies.`
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to reload policy catalog.'
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: `Error: ${error.message || 'An error occurred while reloading the policy catalog.'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReloadLicenses = async () => {
    setLicenseLoading(true);
    setLicenseMessage(null);
    try {
      const response = await apiCall('TCAdminImportLicenseCatalog', 'POST');
      if (response && response.includes('Imported')) {
        setLicenseMessage({
          type: 'success',
          text: response
        });
      } else {
        setLicenseMessage({
          type: 'error',
          text: 'Failed to reload license catalog.'
        });
      }
    } catch (error: any) {
      setLicenseMessage({
        type: 'error',
        text: `Error: ${error.message || 'An error occurred while reloading the license catalog.'}`
      });
    } finally {
      setLicenseLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Policy Maintenance
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Reload Policy Catalog
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will mark all existing policies as inactive and reload the policy catalog from blob storage.
              Use this when new policies have been added to the blob storage or when you need to refresh the catalog.
            </Typography>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleReloadPolicies}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? 'Reloading...' : 'Reload Policies'}
            </Button>
            {message && (
              <Alert severity={message.type} sx={{ mt: 2 }}>
                {message.text}
              </Alert>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Reload License Catalog
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reloads the Microsoft licensing catalog from the official source and updates the LicenseCatalog table. Use this when Microsoft releases new licenses or service plans, or when you need to refresh the catalog for policy validation.
            </Typography>
            <Button
              variant="contained"
              startIcon={licenseLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleReloadLicenses}
              disabled={licenseLoading}
              sx={{ mb: 2 }}
            >
              {licenseLoading ? 'Reloading...' : 'Reload Licenses'}
            </Button>
            {licenseMessage && (
              <Alert severity={licenseMessage.type} sx={{ mt: 2 }}>
                {licenseMessage.text}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}